import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Route, Routes, useSearchParams } from "react-router-dom";
import { NotInLocationPicture } from "../../Assets/assets";
import Button from "../../Components/Button/Button";
import Header from "../../Components/Header/Header";
import { compareCapturedFace } from "../../CustomHooks/useFaceDetection";
import {
  getReferenceImage,
  revertUpdateCapturePicture,
  updateCapturePicture
} from "../../Redux/faceComparison/action";
import { setSelectedLocation } from "../../Redux/locations/action";
import { setErrorStatus } from "../../Redux/status/action";
import { isValidObject } from "../../Services/validators";
import { locationRange } from "../../Utils/constants";
import LocationSearch from "../LocationSearch/LocationSearch";
import ComparisonStatus from "./ComparisonStatus";
import ConfirmManualApproval from "./ConfirmManualApproval";
import EmployeeSearch from "./EmployeeSearch";
import FaceDetectionAndCapture from "./FaceDetectionAndCapture";
import GetNearestLocation from "./GetNearestLocation";
import LatestCaptureData from "./LatestCaptureData";
import ManualApprovalSuccess from "./ManualApprovalSuccess";
import MultipleLocationFound from "./MultipleLocationFound";

function FaceIdentification(props) {
  const [faceComparisonData, setFaceComparisonData] = useState(null);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [capturedId, setCapturedId] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");

  const capturesWithQueuedData = useMemo(() => {
    const { captures, offlineRequests } = props.faceComparison || {};

    const safeOfflineRequests = Array.isArray(offlineRequests)
      ? offlineRequests
      : [];

    const allItems = isValidObject(captures)
      ? [...Object.values(captures), ...safeOfflineRequests]
      : [];

    const getCreatedAt = (item) => {
      if (item.createdAt) return Number(item.createdAt);
      if (item.body?.createdAt) return parseInt(item.body.createdAt, 10);
      return 0;
    };

    return allItems.sort((a, b) => getCreatedAt(b) - getCreatedAt(a));
    // eslint-disable-next-line
  }, [
    props.faceComparison.captures,
    props.faceComparison?.offlineRequests,
    window.location.pathname
  ]);

  const handleReferenceClick = (notMatch, documentId) => {
    if (!selectedEmployee?.roles || selectedEmployee?.roles?.length === 0) {
      return setErrorStatus({
        code: "custom",
        message: "Selected employee has no role(s) assigned."
      });
    }
    if (notMatch) {
      props.revertUpdateCapturePicture(
        {
          name: selectedEmployee?.name,
          employee: {
            id: selectedEmployee?.documentId,
            name: selectedEmployee?.name,
            phoneNumber: selectedEmployee?.phoneNumber,
            roles: selectedEmployee?.roles
          },
          capturedId: documentId || capturedId
        },
        props.navigate
      );
    } else {
      props.updateCapturePicture(
        {
          name: selectedEmployee?.name,
          employee: {
            id: selectedEmployee?.documentId,
            name: selectedEmployee?.name,
            phoneNumber: selectedEmployee.phoneNumber,
            roles: selectedEmployee.roles
          },
          capturedId: documentId || capturedId
        },
        props.navigate
      );
    }
  };

  useEffect(() => {
    if (
      isValidObject(faceComparisonData) &&
      window.location.pathname.includes("/faceIdentification/captureImage")
    ) {
      setFaceComparisonData(null);
    }
    // eslint-disable-next-line
  }, [props.locations.selectedLocation, window.location.pathname]);

  useEffect(() => {
    const isCaptureScreen = window.location.pathname.includes(
      "faceIdentification/captureImage"
    );
    const selectedLocation = props.locations.selectedLocation;
    const locationData = props.locations.data?.[selectedLocation];
    const isLoading = props.locations.loading;

    if (isCaptureScreen && !isLoading && !isValidObject(locationData)) {
      setSelectedLocation(null);
      props.navigate("/faceIdentification/locationSearch");
    }
    // eslint-disable-next-line
  }, [
    props.locations.selectedLocation,
    props.locations.data,
    props.locations.loading
  ]);

  return (
    <div className="inherit-parent-height inherit-parent-width overflow-hidden">
      <Header
        title="Face Identification"
        profileOnClick={() => {
          props.navigate("/settings");
        }}
        theme={
          props.auth.theme.primaryColor === "#0E0E0E"
            ? window.location.pathname.includes(
                "faceIdentification/captureImage"
              ) &&
              (type !== "notInLocation" ||
                props.locations.distance > locationRange ||
                !props.locations.loading)
              ? "light"
              : "dark"
            : "light"
        }
        backOnClick={() => {
          const locationPathName = window.location.pathname;

          if (locationPathName.includes("/faceIdentification/captureImage")) {
            setSelectedLocation(null);
            props.navigate("/faceIdentification/lastCapturedAttendance");
          } else if (
            locationPathName.includes("/faceIdentification/uploadReference")
          ) {
            setSelectedEmployee(null);
            props.navigate("/faceIdentification/employeeSearch");
          } else if (
            locationPathName.includes(
              "/faceIdentification/lastCapturedAttendance"
            )
          ) {
            props.navigate("/");
          } else if (
            locationPathName.includes(
              "/faceIdentification/getNearestLocation"
            ) ||
            type === "list"
          ) {
            props.navigate("/faceIdentification/lastCapturedAttendance");
          } else {
            props.navigate(-1);
          }
        }}
      />

      <div className="remaining-body-height">
        <Routes>
          <Route
            path="/captureImage"
            element={
              <>
                {type !== "notInLocation" &&
                (!props.locations?.distance ||
                  props.locations?.distance > locationRange) &&
                !props.locations.loading ? (
                  <div
                    className="inherit-parent-height display-flex flex-direction-column padding-larger flex-justify-content-space-between flex-align-items-center"
                    data-cy="notInLocation-fallBack"
                  >
                    <div className="">NOT IN LOCATION</div>
                    <div className="inherit-parent-height inherit-parent-width flex-direction-column flex-justify-content-center flex-align-items-center">
                      <NotInLocationPicture />
                      <div className="padding-top-larger text-align-center font-size-default">
                        Your location is not close to the selected PCT
                      </div>
                      <div className="padding-top-medium text-align-center font-size-medium">
                        Proceed to the PCT -{" "}
                        <span
                          style={{
                            fontWeight: "bold"
                          }}
                        >
                          {
                            props.locations.data?.[
                              props.locations.selectedLocation
                            ]?.PCTNo
                          }
                          ,{" "}
                          {
                            props.locations.data?.[
                              props.locations.selectedLocation
                            ]?.location
                          }{" "}
                        </span>
                        to continue.
                      </div>
                    </div>
                    <div className="inherit-parent-width flex-direction-column flex-justify-content-center flex-align-items-center">
                      <div className="flex-align-items-center flex-justify-content-center font-size-small white-space-nowrap">
                        At the spot but shows “Not in location”?&nbsp;
                        <span
                          onClick={() => {
                            props.navigate(
                              "/faceIdentification/captureImage?type=notInLocation"
                            );
                          }}
                          className="font-color-secondaryColor cursor-pointer"
                        >
                          Request verification.
                        </span>
                      </div>
                      <div className="inherit-parent-width flex-center-children-vertically padding-top-default padding-bottom-default">
                        <hr
                          style={{ width: "100%" }}
                          className="display-inline-block border-color-secondary-lite border-top-left-right-none flex-basis-1"
                        />
                        <span className="margin-horizontal-default display-inline-block font-color-secondary font-size-small">
                          OR
                        </span>
                        <hr
                          style={{ width: "100%" }}
                          className="display-inline-block border-color-secondary-lite border-top-left-right-none flex-basis-1"
                        />
                      </div>
                      <Button
                        data-cy="continue-to-home-button"
                        type="button"
                        text="Change Location"
                        boxShadow={false}
                        onClick={() => {
                          props.navigate("/faceIdentification/locationSearch");
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <FaceDetectionAndCapture
                    type={type}
                    navigate={props.navigate}
                    isOnline={props.isOnline}
                    compareCapturedFace={async (image) => {
                      const selectedLocation =
                        props.locations.data?.[
                          props.locations.selectedLocation
                        ];
                      const currentLocation = props.locations.currentLocation;
                      // Selected id no longer resolves (stale persisted id,
                      // location removed from data): pick again instead of
                      // sending a capture with no location details.
                      if (!selectedLocation?.documentId) {
                        setSelectedLocation(null);
                        props.navigate("/faceIdentification/locationSearch");
                        return;
                      }
                      return await compareCapturedFace({
                        photo: image,
                        location: {
                          PCTNo: selectedLocation?.PCTNo,
                          ward: selectedLocation?.ward,
                          location: selectedLocation?.location,
                          zone: selectedLocation?.zone,
                          locationId: selectedLocation?.documentId,
                          ...(type === "notInLocation"
                            ? {
                                geoContext: {
                                  currentLocation: {
                                    lat: currentLocation?.latitude,
                                    lng: currentLocation?.longitude
                                  },
                                  selectedLocation: {
                                    lat: selectedLocation?._geoloc?.lat,
                                    lng: selectedLocation?._geoloc?.lng
                                  }
                                }
                              }
                            : {})
                        },
                        profileData: props.profile.data,
                        accessToken: props.auth.data.accessToken
                      });
                    }}
                    selectedLocation={
                      props.locations.data?.[props.locations.selectedLocation]
                    }
                    setFaceComparisonData={setFaceComparisonData}
                    faceComparison={props.faceComparison}
                    setCapturedPhoto={setCapturedPhoto}
                    capturedPhoto={capturedPhoto}
                    selectedEmployee={selectedEmployee}
                    setCapturedId={setCapturedId}
                  />
                )}
              </>
            }
          />
          <Route
            path="/faceIdentification/locationSearch"
            element={
              <LocationSearch
                auth={props.auth}
                navigate={props.navigate}
                loading={props.locations.loading}
                locations={
                  isValidObject(props.locations.data)
                    ? Object.values(props.locations.data)?.filter(
                        (data) => data.access === "public"
                      )
                    : []
                }
              />
            }
          />

          <Route
            path="/getNearestLocation"
            element={
              <GetNearestLocation
                setSelectedLocation={setSelectedLocation}
                currentLocation={props.locations.currentLocation}
                navigate={props.navigate}
                nearestLocations={props.locations.nearestLocations}
              />
            }
          />
          <Route
            path="/multipleLocationsFound"
            element={
              <MultipleLocationFound
                setSelectedLocation={setSelectedLocation}
                navigate={props.navigate}
                data={props.locations.nearestLocations}
                locations={props.locations.data}
              />
            }
          />

          <Route
            path="/success"
            element={
              <ComparisonStatus
                type={type}
                location={
                  props.locations.selectedLocation &&
                  props.locations.data?.[props.locations.selectedLocation]
                }
                faceComparisonData={faceComparisonData}
                navigate={props.navigate}
              />
            }
          />
          <Route
            path="/failed"
            element={
              <ComparisonStatus
                buttonOnClick={() => {
                  props.navigate("/faceIdentification/captureImage");
                  setCapturedPhoto(null);
                }}
                location={
                  props.locations.selectedLocation &&
                  props.locations.data?.[props.locations.selectedLocation]
                }
                capturedPhoto={capturedPhoto}
                faceComparisonData={faceComparisonData}
                navigate={props.navigate}
              />
            }
          />

          <Route
            path="/offline"
            element={
              <ComparisonStatus
                location={
                  props.locations.selectedLocation &&
                  props.locations.data?.[props.locations.selectedLocation]
                }
                capturedPhoto={capturedPhoto}
                faceComparisonData={faceComparisonData}
                navigate={props.navigate}
              />
            }
          />

          <Route
            path="/pending"
            element={
              <ComparisonStatus
                type={type}
                location={
                  props.locations.selectedLocation &&
                  props.locations.data?.[props.locations.selectedLocation]
                }
                capturedPhoto={capturedPhoto}
                faceComparisonData={faceComparisonData}
                navigate={props.navigate}
              />
            }
          />

          <Route
            path="/denied"
            element={
              <ComparisonStatus
                type={type}
                location={
                  props.locations.selectedLocation &&
                  props.locations.data?.[props.locations.selectedLocation]
                }
                capturedPhoto={capturedPhoto}
                faceComparisonData={faceComparisonData}
                navigate={props.navigate}
              />
            }
          />

          <Route
            path="/employeeSearch"
            element={
              <EmployeeSearch
                faceComparisonData={faceComparisonData}
                capturedPhoto={capturedPhoto}
                capturedId={capturedId}
                setSelectedEmployee={setSelectedEmployee}
                navigate={props.navigate}
              />
            }
          />
          <Route
            path="/lastCapturedAttendance"
            element={
              <LatestCaptureData
                selectedLocation={props.locations.selectedLocation}
                data={capturesWithQueuedData}
                navigate={props.navigate}
                setCapturedId={setCapturedId}
                setFaceComparisonData={setFaceComparisonData}
                getReferenceImage={getReferenceImage}
              />
            }
          />
          <Route
            path="/uploadReference"
            element={
              <ConfirmManualApproval
                type={type}
                faceComparisonData={faceComparisonData}
                selectedEmployee={selectedEmployee}
                capturedPhoto={capturedPhoto}
                navigate={props.navigate}
                loading={props.faceComparison.loading}
                confirmOnClick={(notMatch, documentId) => {
                  handleReferenceClick(notMatch, documentId);
                }}
                onCancelClick={() => {
                  setCapturedPhoto(null);
                  setFaceComparisonData(null);
                  props.navigate("/faceIdentification/lastCapturedAttendance");
                  setFaceComparisonData(null);
                  props.navigate("/faceIdentification/lastCapturedAttendance");
                }}
                capturedId={capturedId}
              />
            }
          />
          <Route
            path="/uploadReference/success"
            element={
              <ManualApprovalSuccess
                selectedEmployee={selectedEmployee}
                capturedPhoto={capturedPhoto}
                faceComparisonData={faceComparisonData}
                navigate={props.navigate}
                location={
                  props.locations.selectedLocation &&
                  props.locations.data?.[props.locations.selectedLocation]
                }
              />
            }
          />
        </Routes>
      </div>
    </div>
  );
}

const mapStateToProps = function (state) {
  return {
    faceComparison: state.faceComparison,
    locations: state.locations,
    profile: state.profile,
    auth: state.auth
  };
};

const mapDispatchToProps = function () {
  return {
    updateCapturePicture: (data, navigate) =>
      updateCapturePicture(data, navigate),
    revertUpdateCapturePicture: (data, navigate) =>
      revertUpdateCapturePicture(data, navigate)
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(FaceIdentification);
